/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lookup_building_classes-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lookup_building_classes-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lookup_building_classes-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, archived, sort_order, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lookup_building_classes-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lookup_building_classes-api-client.ts (API client)
 * - client/api/generated_schemas/lookup_building_classes.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lookup_building_classes-entity-config.ts
 * Public fields: 8/8 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LookupBuildingClassesClient, LookupBuildingClassesQueryOptions } from '../generated_clients/generated-lookup_building_classes-api-client.js';
import {
  LookupBuildingClassesInsert,
  LookupBuildingClassesUpdate,
  LookupBuildingClassesFieldConfig
} from '../../types/generated_schemas/generated-lookup_building_classes-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lookup_building_classes_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const lookupBuildingClassesKeys = {
  all: ['lookup_building_classes'] as const,
  lists: () => [...lookupBuildingClassesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LookupBuildingClassesQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...lookupBuildingClassesKeys.lists(), { filters, ...page }] as const,
  details: () => [...lookupBuildingClassesKeys.all, 'detail'] as const,
  detail: (id: string) => [...lookupBuildingClassesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lookupBuildingClassesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lookupBuildingClassesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lookupBuildingClassesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lookupBuildingClassesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLookupBuildingClassesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LookupBuildingClassesClient();
};

/**
 * Hook to get all lookup_building_classes with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lookup_building_classes data
 */
export const useLookupBuildingClassesList = (options?: LookupBuildingClassesQueryOptions) => {
  const client = useLookupBuildingClassesClient();
  
  return useQuery({
    queryKey: lookupBuildingClassesKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lookup_building_classe by ID
 *
 * @param id - The lookup_building_classe ID
 * @returns React Query result with single lookup_building_classe data
 */
export const useLookupBuildingClasses = (id: string) => {
  const client = useLookupBuildingClassesClient();
  
  return useQuery({
    queryKey: lookupBuildingClassesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lookup_building_classes
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lookup_building_classes_business_hooks/
 */

/**
 * Hook to create a new lookup_building_classe
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, archived, sort_order, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating lookup_building_classe
 */
export const useCreateLookupBuildingClasses = () => {
  const client = useLookupBuildingClassesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LookupBuildingClassesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupBuildingClassesKeys.all });
    },
  });
};

/**
 * Hook to update an existing lookup_building_classe
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating lookup_building_classe
 */
export const useUpdateLookupBuildingClasses = () => {
  const client = useLookupBuildingClassesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LookupBuildingClassesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lookupBuildingClassesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lookupBuildingClassesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lookup_building_classe
 *
 * @returns React Query mutation for deleting lookup_building_classe
 */
export const useDeleteLookupBuildingClasses = () => {
  const client = useLookupBuildingClassesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupBuildingClassesKeys.all });
    },
  });
};

/**
 * Hook to count lookup_building_classes with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLookupBuildingClassesCount = (filter?: Partial<Record<typeof LookupBuildingClassesFieldConfig.filterable[number], any>>) => {
  const client = useLookupBuildingClassesClient();
  
  return useQuery({
    queryKey: lookupBuildingClassesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLookupBuildingClassesFieldConfig = () => {
  return LookupBuildingClassesFieldConfig;
};